/** @jsx jsx */

import { isEmpty } from 'lodash';
import { Fragment, SyntheticEvent, useCallback } from 'react';

import {
  Alert,
  Icon,
  LegacyButton,
  Margin,
  Text,
  TooltipWrapper,
} from '@zapier/design-system';
import { ZapierLogo } from '@zapier/design-system-patterns';
import { jsx, css } from '@emotion/core';
import { Icon as ZinniaIcon } from '@zapier/zinnia-icons';

import { useLink } from '@zapier/common-hooks';

import ActionsDropdownMenu from 'app/developer-v3/components/ActionsDropdownMenu';
import ByCliToolMessage from 'app/developer-v3/components/ByCliToolMessage';
import ClickableTable from 'app/developer-v3/components/ClickableTable';
import LockedNavCog from 'app/developer-v3/components/LockedNavCog';

import HelpBox from 'app/developer-v3/components/HelpBox';
import ManagerLayout from 'app/developer-v3/layouts/ManagerLayout';
import Link from 'app/common/components/Link';
import {
  CurrentVersionReadOnlyReason,
  useActiveProfile,
  useCurrentVersionReadOnlyReason,
} from 'app/developer-v3/hooks';
import {
  ACTION_TYPES,
  BASE_DOC_URL_VB,
  NEW_SERVICE_FIELD,
} from 'app/developer-v3/constants';
import {
  getActionUrl,
  isLastDescendantNodeClicked,
} from 'app/developer-v3/utils';

import CustomActions from './CustomActions';
import { isExternalAction } from './utils';

import type {
  ActionParams,
  ActionSchema,
  ActionType,
  AppDefinitionProps,
  AppSummary,
} from 'app/developer-v3/types';

type Action = {
  serviceType: ActionType;
} & ActionSchema;

type Props = AppDefinitionProps & {
  actions: Action[];
  appSummary: AppSummary;
  deleteAction: (type: ActionType, actionKey: string) => void;
  params: ActionParams;
  selected_api: string;
};

const Styles = {
  labelTextWrapper: css`
    align-items: center;
    display: flex;
    gap: 5px;
  `,
};

/**
 * Main page for managing an app's Actions
 */

const ActionsPage = (props: Props) => {
  const { actions, appSummary, deleteAction, params, selected_api } = props;
  const { isGUIIntegration } = appSummary;

  const { routeTo } = useLink();
  const currentVersionReadOnlyReason = useCurrentVersionReadOnlyReason();
  const isReadOnly = !!currentVersionReadOnlyReason;
  const isNotAdmin =
    currentVersionReadOnlyReason === CurrentVersionReadOnlyReason.NOT_ADMIN;

  const { isLoading: isLoadingProfile, activeProfile } = useActiveProfile();
  const isStaff = !isLoadingProfile && activeProfile?.isStaff;

  const getCellModifiers = useCallback(
    (iCol: number, iRow: number) => {
      const action = actions[iRow];

      return {
        darken: iCol < 2,
        disabled: isExternalAction(action)
          ? !isStaff
          : !isGUIIntegration || isNotAdmin,
      };
    },
    [actions, isGUIIntegration, isNotAdmin, isStaff]
  );

  const onClickRow = useCallback(
    (event: SyntheticEvent<HTMLElement>, index: number) => {
      const action = actions[index];

      if (isExternalAction(action)) {
        const actionUrl = action.links?.actionUrl;
        if (isStaff && actionUrl) {
          routeTo(actionUrl);
        }
      } else {
        if (
          isGUIIntegration &&
          !isNotAdmin &&
          !isLastDescendantNodeClicked(event)
        ) {
          routeTo(
            getActionUrl({
              id: params.id,
              version: params.version,
              actionKey: actions[index].key,
              actionType: actions[index].serviceType,
            })
          );
        }
      }
    },
    [
      actions,
      isGUIIntegration,
      isNotAdmin,
      isStaff,
      params.id,
      params.version,
      routeTo,
    ]
  );

  return (
    <ManagerLayout
      afterHeader={
        <LegacyButton
          color="primary"
          disabled={!isGUIIntegration || isReadOnly}
          href={getActionUrl({
            id: params.id,
            version: params.version,
            actionKey: NEW_SERVICE_FIELD,
            actionType: ACTION_TYPES.creates,
          })}
          iconBefore="formAdd"
        >
          Add Action
        </LegacyButton>
      }
      afterRender={
        isStaff ? (
          <Margin margin="25px 0px 0px">
            <CustomActions selectedApi={selected_api.split('@')[0]} />
          </Margin>
        ) : null
      }
      title="Actions"
    >
      {!isGUIIntegration && (
        <Alert>
          <ByCliToolMessage theme="light" />
        </Alert>
      )}
      {isGUIIntegration && isEmpty(actions) && (
        <HelpBox
          description={
            <span>
              Actions POST or send data to your API, to find, update, or create
              items using data from Triggers and previous Actions in the Zap.
              Add actions for everything users can find, add, or update in your
              app.{' '}
              <Link href={`${BASE_DOC_URL_VB}/build/action`} target="_blank">
                Learn more
              </Link>
              .
            </span>
          }
          heading="Add Your First Action"
        />
      )}
      {!isEmpty(actions) && (
        <ClickableTable
          body={actions.map(action => {
            const { display, key, serviceType } = action;

            return [
              // Name column (Action Label)
              {
                value: display.label,
                element: (
                  <Fragment>
                    <TooltipWrapper
                      allowMultilineTooltip={true}
                      content={
                        isExternalAction(action) && !isStaff ? (
                          <Text color={'inherit'}>
                            This action was created by Zapier, you don't have
                            edit access.{' '}
                            <Link
                              color={'secondary'}
                              href={`${BASE_DOC_URL_VB}/manage/versions#managing-versions-in-platform-cli`}
                              target="_blank"
                            >
                              Learn more
                            </Link>
                            .
                          </Text>
                        ) : !isExternalAction(action) && !isGUIIntegration ? (
                          <ByCliToolMessage theme="dark" />
                        ) : null
                      }
                      position="southwest"
                    >
                      {() => (
                        <div css={Styles.labelTextWrapper}>
                          <Fragment>
                            <Text type="Body4">{display.label}</Text>
                            {isExternalAction(action) ? (
                              <Fragment>
                                <div
                                  style={{
                                    alignContent: 'center',
                                    backgroundColor: '#201515',
                                    borderRadius: 5,
                                    display: 'grid',
                                    height: 20,
                                    justifyContent: 'center',
                                    width: 70,
                                  }}
                                >
                                  <ZapierLogo
                                    height={14}
                                    variant="wordmark-horizontal-inverse"
                                  />
                                </div>
                                {!isStaff && (
                                  <Icon name="actionLock" size={20} />
                                )}
                              </Fragment>
                            ) : (
                              !isGUIIntegration && (
                                <Icon name="actionLock" size={20} />
                              )
                            )}
                          </Fragment>
                        </div>
                      )}
                    </TooltipWrapper>
                    <Text tag="p">{display.description}</Text>
                  </Fragment>
                ),
              },
              // Key column
              { value: key, element: key },
              // Visibility column
              {
                value: display.hidden ? 'Hidden' : 'Shown',
                element: <Text>{display.hidden ? 'Hidden' : 'Shown'}</Text>,
              },
              // Origin column
              {
                value: isExternalAction(action)
                  ? 'External action'
                  : isGUIIntegration
                  ? 'UI'
                  : 'CLI',
                element: (
                  <Text>
                    {isExternalAction(action)
                      ? 'External action'
                      : isGUIIntegration
                      ? 'UI'
                      : 'CLI'}
                  </Text>
                ),
              },
              // Actions column
              {
                value: '',
                element: isNotAdmin ? (
                  <LockedNavCog message="Admin access only" />
                ) : (
                  !isExternalAction(action) &&
                  isGUIIntegration &&
                  !isReadOnly && (
                    <ActionsDropdownMenu
                      confirmationText="Delete"
                      items={[
                        {
                          content: (
                            <Fragment>
                              <ZinniaIcon name="actionEdit" size={16} /> Edit
                            </Fragment>
                          ),
                          value: 'edit',
                          onChoose: () =>
                            routeTo(
                              getActionUrl({
                                id: params.id,
                                version: params.version,
                                actionKey: key,
                                actionType: serviceType,
                              })
                            ),
                        },
                        {
                          isConfirmationRequired: true,
                          isDanger: true,
                          content: (
                            <Fragment>
                              <ZinniaIcon name="actionTrash" size={16} /> Delete
                            </Fragment>
                          ),
                          modalTextContent: `Are you sure you want to delete the action with key: ${key}?`,
                          modalTitle: `Delete ${action.display.label} action?`,
                          onChoose: () => {
                            deleteAction(serviceType, key);
                          },
                          value: 'delete',
                        },
                      ]}
                    />
                  )
                ),
              },
            ];
          })}
          getCellModifiers={getCellModifiers}
          header={[
            { title: 'Name', hasSort: true },
            { title: 'Key', hasSort: false },
            { title: 'Visibility in Editor', hasSort: true },
            { title: 'Origin', hasSort: true },
            { title: '', hasSort: false },
          ]}
          onClickRow={onClickRow}
          rowAriaLabel="Click to edit this action"
        />
      )}
    </ManagerLayout>
  );
};

export default ActionsPage;
